import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { analysisService } from '../../services/analysisService';
import { userService } from '../../services/userService';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faRobot
} from '@fortawesome/free-solid-svg-icons';
import { SectionType } from '../../types';
import SimplificationModal from '../../components/analysis/SimplificationModal';
import { RichTextEditor } from '../../components/ui/RichTextEditor';
import toast from 'react-hot-toast';

// Section type Spanish labels
const sectionLabelMap: Record<SectionType, string> = {
  HEADING_1: 'Título Principal',
  HEADING_2: 'Subtítulo',
  HEADING_3: 'Encabezado',
  PARAGRAPH: 'Párrafo',
  LIST_ITEM: 'Elemento de Lista',
  TABLE: 'Tabla',
  QUOTE: 'Cita',
};

export default function SectionResultPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [showSimplificationModal, setShowSimplificationModal] = useState(false);
  const [simplificationResult, setSimplificationResult] = useState<{
    originalText: string;
    simplifiedText: string;
    provider: string;
    targetLevel: string;
  } | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [showingVersion, setShowingVersion] = useState<'original' | 'edited'>('edited');

  // Estado para validaciones manuales de criterios
  const [criteriaValidations, setCriteriaValidations] = useState<{ [key: string]: boolean | null }>({});

  const { data, isLoading } = useQuery({
    queryKey: ['section', id],
    queryFn: () => analysisService.getSectionById(Number(id)),
    enabled: !!id,
  });

  // Get user preferences for AI simplification
  const { data: preferencesData } = useQuery({
    queryKey: ['userPreferences'],
    queryFn: () => userService.getPreferences(),
  });

  // Set initial version based on whether editedContent exists
  useEffect(() => {
    if (data?.section) {
      setShowingVersion(data.section.editedContent ? 'edited' : 'original');
    }
  }, [data?.section]);

  // Load saved validations from database (use SECTION endpoint)
  useEffect(() => {
    if (id) {
      analysisService.getSectionValidations(Number(id))
        .then(result => {
          if (result.validations && Object.keys(result.validations).length > 0) {
            setCriteriaValidations(result.validations);
          }
        })
        .catch(error => {
          console.error('Error loading validations:', error);
        });
    }
  }, [id]);

  // Mutation for saving validations (use SECTION endpoint)
  const saveValidationsMutation = useMutation({
    mutationFn: (validations: { [key: string]: boolean | null }) =>
      analysisService.updateSectionValidations(Number(id), validations),
    onSuccess: () => {
      // Invalidate all grouped analyses queries to refresh history lists
      queryClient.invalidateQueries({ queryKey: ['grouped-analyses'] });
      // Also invalidate analysis-sections queries to refresh section lists
      queryClient.invalidateQueries({ queryKey: ['analysis-sections'] });
      // Also invalidate the current section to ensure it's up-to-date
      queryClient.invalidateQueries({ queryKey: ['section', id] });
    },
    onError: (error: any) => {
      console.error('Error saving validations:', error);
      toast.error('Error al guardar las validaciones');
    },
  });

  // Mutation for simplifying section text
  const simplifyMutation = useMutation({
    mutationFn: () => analysisService.simplifySectionText(Number(id)),
    onSuccess: (result) => {
      setSimplificationResult({
        originalText: result.originalText,
        simplifiedText: result.simplifiedText,
        provider: result.provider,
        targetLevel: result.targetLevel,
      });
      setShowSimplificationModal(true);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al simplificar el texto');
    },
  });

  const handleSimplify = () => {
    simplifyMutation.mutate();
  };

  // Mutation for saving edited text
  const saveEditedTextMutation = useMutation({
    mutationFn: async (text: string) => {
      const sectionId = parseInt(id!);
      return analysisService.updateSectionWithEditedContent(sectionId, text);
    },
    onSuccess: async (response) => {
      toast.success('Sección actualizada con el contenido editado');
      // Update cache directly with new data
      queryClient.setQueryData(['section', id], response);
      // Also invalidate to ensure freshness
      await queryClient.invalidateQueries({ queryKey: ['section', id] });
      await queryClient.invalidateQueries({ queryKey: ['analysis-sections'] });
      setIsEditMode(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al actualizar la sección');
    }
  });

  const handleEnterEditMode = () => {
    // Use the currently displayed version as the starting point for editing
    const textToEdit = showingVersion === 'edited' && section.editedContent
      ? section.editedContent
      : section.content;
    setEditedText(textToEdit);
    setIsEditMode(true);
  };

  const handleExitEditMode = () => {
    setIsEditMode(false);
    setEditedText('');
  };

  const handleSaveEditedText = () => {
    if (editedText.trim().length < 10) {
      toast.error('El texto debe tener al menos 10 caracteres');
      return;
    }
    saveEditedTextMutation.mutate(editedText);
  };

  // Mutation for toggling audit status of THIS section
  const toggleAuditStatusMutation = useMutation({
    mutationFn: async () => {
      const sectionId = parseInt(id!);
      return analysisService.toggleSectionAuditStatus(sectionId);
    },
    onSuccess: async (response) => {
      toast.success(response.message);
      // Update cache directly with new section data
      queryClient.setQueryData(['section', id], response);
      // Invalidate to refresh data
      await queryClient.invalidateQueries({ queryKey: ['section', id] });
      await queryClient.invalidateQueries({ queryKey: ['analysis-sections'] });
      await queryClient.invalidateQueries({ queryKey: ['grouped-analyses'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al cambiar el estado de auditoría');
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando análisis...</p>
        </div>
      </div>
    );
  }

  if (!data?.section) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
            <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <p className="text-muted-foreground mb-4">Análisis no encontrado</p>
          <Button onClick={() => navigate('/history')}>
            Volver al historial
          </Button>
        </CardContent>
      </Card>
    );
  }

  const section = data.section;
  const metadata = section.sectionMetadata || {};

  // Tab: Resumen General
  function renderOverviewTab() {
    return (
      <div className="space-y-6">
        {/* Texto Original */}
        <Card>
          <CardContent className="p-6">
            {/* Version Control UI */}
            {section.editedContent && !isEditMode && (
              <div className="mb-3 flex items-center justify-between gap-3 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-blue-900">Versión:</span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={showingVersion === 'edited' ? 'default' : 'outline'}
                      onClick={() => setShowingVersion('edited')}
                    >
                      Editada (Actual)
                    </Button>
                    <Button
                      size="sm"
                      variant={showingVersion === 'original' ? 'default' : 'outline'}
                      onClick={() => setShowingVersion('original')}
                    >
                      Original
                    </Button>
                  </div>
                </div>
                {showingVersion === 'original' && (
                  <Button onClick={() => saveEditedTextMutation.mutate(section.content)}>
                    Volver a analizar original
                  </Button>
                )}
              </div>
            )}

            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Texto Analizado</h3>
              <div className="flex items-center gap-2">
                {!isEditMode && (
                  <>
                    <Button
                      onClick={handleEnterEditMode}
                      size="sm"
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faFileAlt} />
                      Editar Texto
                    </Button>
                    {preferencesData?.preferences?.enableAiSimplification && (
                      <Button
                        onClick={handleSimplify}
                        disabled={simplifyMutation.isPending}
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        <FontAwesomeIcon icon={faRobot} />
                        {simplifyMutation.isPending ? 'Simplificando...' : 'Simplificar con IA'}
                      </Button>
                    )}
                  </>
                )}
                {isEditMode && (
                  <>
                    <Button
                      onClick={handleSaveEditedText}
                      disabled={saveEditedTextMutation.isPending}
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faCheckCircle} />
                      {saveEditedTextMutation.isPending ? 'Analizando...' : 'Guardar y Analizar'}
                    </Button>
                    <Button
                      onClick={handleExitEditMode}
                      disabled={saveEditedTextMutation.isPending}
                      size="sm"
                      variant="outline"
                    >
                      Cancelar Edición
                    </Button>
                  </>
                )}
              </div>
            </div>
            {!isEditMode ? (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
                <div className="prose prose-sm max-w-none formatted-content" dangerouslySetInnerHTML={{
                  __html: showingVersion === 'edited' && section.editedContent
                    ? section.editedContent
                    : section.content
                }} />
              </div>
            ) : (
              <RichTextEditor
                content={editedText}
                onChange={(content) => setEditedText(content)}
                placeholder="Escribe o edita tu texto aquí..."
              />
            )}

            {/* CSS Styles for formatted content display */}
            <style>{`
              .formatted-content ul,
              .formatted-content ol {
                padding-left: 1.5rem;
                margin: 0.5rem 0;
              }

              .formatted-content ul {
                list-style-type: disc;
              }

              .formatted-content ol {
                list-style-type: decimal;
              }

              .formatted-content li {
                margin: 0.25rem 0;
              }

              .formatted-content p {
                margin: 0.5rem 0;
              }

              .formatted-content strong {
                font-weight: bold;
              }

              .formatted-content em {
                font-style: italic;
              }

              .formatted-content u {
                text-decoration: underline;
              }

              .formatted-content [style*="text-align: center"] {
                text-align: center;
              }

              .formatted-content [style*="text-align: right"] {
                text-align: right;
              }

              .formatted-content [style*="text-align: justify"] {
                text-align: justify;
              }
            `}</style>
            <div className="mt-3 text-xs text-gray-500">
              {isEditMode ? (
                <span>{editedText.length} caracteres</span>
              ) : (
                <span>{section.totalWords} palabras • {section.totalSentences} oraciones</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Evaluación según UNE-ISO 24495-1:2024 */}
        {metadata.b2Criteria && (() => {
          const b2Data = metadata.b2Criteria;
          const allCriteria = b2Data.criteria || [];

          // Clasificar criterios por principio UNE (misma lógica que en renderB2CriteriaTab)
          const pertinenciaCriteria = allCriteria.filter((c: any) =>
            c.name.toLowerCase().includes('adecuación al público') ||
            c.name.toLowerCase().includes('propósito claro') ||
            c.name.toLowerCase().includes('suficiencia') ||
            c.name.toLowerCase().includes('superflua')
          );

          const facilidadBusquedaCriteria = allCriteria.filter((c: any) =>
            (c.name.toLowerCase().includes('estructura clara') && c.name.toLowerCase().includes('títulos')) ||
            c.name.toLowerCase().includes('listas') ||
            c.name.toLowerCase().includes('viñetas') ||
            c.name.toLowerCase().includes('ilustraciones') ||
            (c.name.toLowerCase().includes('gráficos') && c.name.toLowerCase().includes('iconos'))
          );

          const comprensibilidadCriteria = allCriteria.filter((c: any) => {
            if (c.name.toLowerCase().includes('listas') ||
                c.name.toLowerCase().includes('viñetas') ||
                c.name.toLowerCase().includes('ilustraciones') ||
                c.name.toLowerCase().includes('gráficos') ||
                (c.name.toLowerCase().includes('iconos') && c.name.toLowerCase().includes('glosario'))) {
              return false;
            }

            return (
              ['legibilidad', 'vocabulario', 'claridad'].includes(c.category?.toLowerCase()) ||
              (c.category?.toLowerCase() === 'estructura' &&
               (c.name.toLowerCase().includes('voz activa') ||
                c.name.toLowerCase().includes('conectores') ||
                c.name.toLowerCase().includes('subordinadas'))) ||
              (c.category?.toLowerCase() === 'formato' && c.name.toLowerCase().includes('mayúsculas')) ||
              c.name.toLowerCase().includes('inflesz') ||
              c.name.toLowerCase().includes('glosario') ||
              c.name.toLowerCase().includes('negaciones') ||
              c.name.toLowerCase().includes('indeterminadas') ||
              c.name.toLowerCase().includes('tono y estilo')
            );
          });

          const usabilidadCriteria = allCriteria.filter((c: any) =>
            c.name.toLowerCase().includes('validación') ||
            c.name.toLowerCase().includes('usuarios reales') ||
            c.name.toLowerCase().includes('orientación a la acción') ||
            c.name.toLowerCase().includes('siguientes pasos')
          );

          // Calcular estado de cada principio (usando pesos para Comprensibilidad)
          const calculatePrincipleStatus = (criteria: any[], useWeights: boolean = false) => {
            if (criteria.length === 0) return { passes: false, percentage: 0, validated: 0, total: 0 };

            if (useWeights) {
              let earnedScore = 0;
              let maxScore = 0;

              criteria.forEach((criterion, index) => {
                const criterionKey = `${criterion.name}-${index}`;
                const validation = criteriaValidations[criterionKey] !== undefined
                  ? criteriaValidations[criterionKey]
                  : (!criterion.scientific ? null : criterion.status === 'pass');

                const weight = criterion.weight || 0;
                maxScore += weight;

                if (validation === true) {
                  earnedScore += weight;
                }
              });

              const percentage = maxScore > 0 ? (earnedScore / maxScore) * 100 : 0;
              return {
                passes: percentage >= 85,
                percentage: percentage,
                validated: Math.round(earnedScore),
                total: maxScore
              };
            } else {
              let passedCount = 0;
              let totalCriteria = criteria.length;

              criteria.forEach((criterion, index) => {
                const criterionKey = `${criterion.name}-${index}`;
                const validation = criteriaValidations[criterionKey] !== undefined
                  ? criteriaValidations[criterionKey]
                  : (!criterion.scientific ? null : criterion.status === 'pass');

                if (validation === true) {
                  passedCount++;
                }
              });

              const percentage = (passedCount / totalCriteria) * 100;
              return {
                passes: percentage >= 85,
                percentage: percentage,
                validated: passedCount,
                total: totalCriteria
              };
            }
          };

          // Calcular estados
          // Añadir los 3 criterios manuales de Pertinencia
          const manualPertinenciaCriteria = [
            {
              name: 'Adecuación al Público Objetivo',
              category: 'pertinencia',
              description: 'Verificar que el documento se ha escrito pensando en el perfil del lector',
              scientific: false,
              status: null
            },
            {
              name: 'Propósito Claro',
              category: 'pertinencia',
              description: 'Evaluar si el documento responde directamente al objetivo del lector',
              scientific: false,
              status: null
            },
            {
              name: 'Suficiencia de la Información',
              category: 'pertinencia',
              description: 'Verificar que contiene toda la información clave sin incluir contenido superfluo',
              scientific: false,
              status: null
            }
          ];
          const pertinenciaStatus = calculatePrincipleStatus([...manualPertinenciaCriteria, ...pertinenciaCriteria], false);
          const facilidadStatus = facilidadBusquedaCriteria.length > 0
            ? calculatePrincipleStatus(facilidadBusquedaCriteria, false)
            : { passes: true, percentage: 100, validated: 0, total: 0 };
          const comprensibilidadStatus = calculatePrincipleStatus(comprensibilidadCriteria, true);
          const usabilidadStatus = usabilidadCriteria.length > 0
            ? calculatePrincipleStatus(usabilidadCriteria, false)
            : { passes: true, percentage: 100, validated: 0, total: 0 };

          const cumpleLenguajeClaro = pertinenciaStatus.passes && facilidadStatus.passes && comprensibilidadStatus.passes && usabilidadStatus.passes;

          return (
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-1">Evaluación según UNE-ISO 24495-1:2024</h3>
                    <p className="text-sm text-gray-600">Criterios organizados por los 4 principios de Lenguaje Claro</p>
                  </div>
                  <div className={`px-6 py-3 rounded-lg text-sm font-bold border-2 ${
                    cumpleLenguajeClaro
                      ? 'bg-green-100 text-green-800 border-green-400'
                      : 'bg-red-100 text-red-800 border-red-400'
                  }`}>
                    {cumpleLenguajeClaro ? '✓ CUMPLE LENGUAJE CLARO' : '✗ NO CUMPLE LENGUAJE CLARO'}
                  </div>
                </div>

                {/* Grid de 4 principios */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Principio 1: Pertinencia */}
                  <div className={`p-4 rounded-lg border-2 ${
                    pertinenciaStatus.passes
                      ? 'bg-green-50 border-green-300'
                      : 'bg-red-50 border-red-300'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-gray-800">Principio 1. Pertinencia</h4>
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${
                        pertinenciaStatus.passes
                          ? 'bg-green-200 text-green-800'
                          : 'bg-red-200 text-red-800'
                      }`}>
                        {pertinenciaStatus.passes ? '✓ PASA' : '✗ NO PASA'}
                      </span>
                    </div>
                    <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          pertinenciaStatus.passes
                            ? 'bg-gradient-to-r from-green-500 to-green-600'
                            : 'bg-gradient-to-r from-red-500 to-orange-500'
                        }`}
                        style={{ width: `${Math.min(pertinenciaStatus.percentage, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      {pertinenciaStatus.validated}/{pertinenciaStatus.total} criterios ({pertinenciaStatus.percentage.toFixed(0)}%)
                    </p>
                  </div>

                  {/* Principio 2: Facilidad de Búsqueda */}
                  <div className={`p-4 rounded-lg border-2 ${
                    facilidadStatus.passes
                      ? 'bg-green-50 border-green-300'
                      : 'bg-red-50 border-red-300'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-gray-800">Principio 2. Facilidad de Búsqueda</h4>
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${
                        facilidadStatus.passes
                          ? 'bg-green-200 text-green-800'
                          : 'bg-red-200 text-red-800'
                      }`}>
                        {facilidadStatus.passes ? '✓ PASA' : '✗ NO PASA'}
                      </span>
                    </div>
                    <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          facilidadStatus.passes
                            ? 'bg-gradient-to-r from-green-500 to-green-600'
                            : 'bg-gradient-to-r from-red-500 to-orange-500'
                        }`}
                        style={{ width: `${Math.min(facilidadStatus.percentage, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      {facilidadStatus.validated}/{facilidadStatus.total} criterios ({facilidadStatus.percentage.toFixed(0)}%)
                    </p>
                  </div>

                  {/* Principio 3: Comprensibilidad */}
                  <div className={`p-4 rounded-lg border-2 ${
                    comprensibilidadStatus.passes
                      ? 'bg-green-50 border-green-300'
                      : 'bg-red-50 border-red-300'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-gray-800">Principio 3. Comprensibilidad (B2)</h4>
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${
                        comprensibilidadStatus.passes
                          ? 'bg-green-200 text-green-800'
                          : 'bg-red-200 text-red-800'
                      }`}>
                        {comprensibilidadStatus.passes ? '✓ PASA' : '✗ NO PASA'}
                      </span>
                    </div>
                    <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          comprensibilidadStatus.passes
                            ? 'bg-gradient-to-r from-green-500 to-green-600'
                            : 'bg-gradient-to-r from-red-500 to-orange-500'
                        }`}
                        style={{ width: `${Math.min(comprensibilidadStatus.percentage, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      Ponderado: {comprensibilidadStatus.percentage.toFixed(0)}% ({comprensibilidadStatus.validated}/{comprensibilidadStatus.total})
                    </p>
                  </div>

                  {/* Principio 4: Usabilidad */}
                  <div className={`p-4 rounded-lg border-2 ${
                    usabilidadStatus.passes
                      ? 'bg-green-50 border-green-300'
                      : 'bg-red-50 border-red-300'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-bold text-gray-800">Principio 4. Usabilidad</h4>
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${
                        usabilidadStatus.passes
                          ? 'bg-green-200 text-green-800'
                          : 'bg-red-200 text-red-800'
                      }`}>
                        {usabilidadStatus.passes ? '✓ PASA' : '✗ NO PASA'}
                      </span>
                    </div>
                    <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          usabilidadStatus.passes
                            ? 'bg-gradient-to-r from-green-500 to-green-600'
                            : 'bg-gradient-to-r from-red-500 to-orange-500'
                        }`}
                        style={{ width: `${Math.min(usabilidadStatus.percentage, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      {usabilidadStatus.validated}/{usabilidadStatus.total} criterios ({usabilidadStatus.percentage.toFixed(0)}%)
                    </p>
                  </div>
                </div>

                {/* Mensaje informativo */}
                <div className="mt-4 p-3 bg-blue-100 border border-blue-300 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Umbral requerido:</strong> Para cumplir con Lenguaje Claro según UNE-ISO 24495-1:2024,
                    cada uno de los 4 principios debe alcanzar al menos el 85% de cumplimiento.
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Metrics Grid - HIDDEN: These metrics are not shown in individual document/URL analysis views per user request */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <MetricCard
            title="INFLESZ"
            value={section.infleszScore != null ? Number(section.infleszScore).toFixed(1) : 'N/A'}
            icon={<FontAwesomeIcon icon={faChartLine} />}
            color="purple"
          />
          <MetricCard
            title="Palabras"
            value={section.totalWords?.toString() || '0'}
            icon={<FontAwesomeIcon icon={faFileAlt} />}
            color="green"
          />
        </div> */}

        {/* Text Details - HIDDEN: These metrics are not shown in individual document/URL analysis views per user request */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold mb-2 text-foreground">Promedio palabras/oración</h3>
              <p className="text-2xl font-bold text-primary">
                {section.avgWordsPerSentence != null ? Number(section.avgWordsPerSentence).toFixed(1) : 'N/A'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {section.avgWordsPerSentence != null && Number(section.avgWordsPerSentence) <= 15
                  ? <><FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mr-1" /> Cumple criterio B2 (≤15)</>
                  : <><FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 mr-1" /> Supera recomendación B2</>}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold mb-2 text-foreground">Promedio sílabas/palabra</h3>
              <p className="text-2xl font-bold text-fuchsia-600">
                {section.avgSyllablesPerWord != null ? Number(section.avgSyllablesPerWord).toFixed(2) : 'N/A'}
              </p>
            </CardContent>
          </Card>
        </div> */}

        {/* Términos que Requieren Glosario */}
        {metadata.glossaryTerms && (
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-bold mb-4">Términos que Requieren Glosario</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Identifica palabras técnicas, especializadas o de alta complejidad (niveles C1-C2) y siglas que necesitan explicación para lectores de nivel básico-intermedio. Las palabras B2 son comprensibles y NO requieren glosario.
              </p>

              {metadata.glossaryTerms.count > 0 ? (
                <div className="space-y-6">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="text-amber-800 font-semibold mb-2">
                      {metadata.glossaryTerms.count} términos encontrados
                    </h4>
                    <p className="text-sm text-amber-700">
                      El texto contiene {metadata.glossaryTerms.count} palabras de niveles C1-C2 y siglas que podrían requerir definición en un glosario.
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {metadata.glossaryTerms.terms.map((term: string, i: number) => (
                      <span key={i} className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                        {term}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-green-800 font-semibold mb-2">¡Excelente! No se encontraron términos que requieran glosario.</h4>
                  <p className="text-sm text-green-700">
                    El texto analizado no contiene términos de alta o media prioridad.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Tab: Criterios de Lenguaje Claro
  function renderB2CriteriaTab() {
    if (!metadata.b2Criteria) {
      return <div className="text-center text-gray-500 py-8">No hay datos de criterios de Lenguaje Claro disponibles</div>;
    }

    // Obtener todos los criterios
    const allCriteria = metadata.b2Criteria.criteria;

    // Clasificar criterios según los 4 principios de la norma UNE-ISO 24495-1:2024

    // 1. PERTINENCIA: ¿Es la información que el lector necesita?
    const pertinenciaCriteria = allCriteria.filter((c: any) =>
      c.name.toLowerCase().includes('adecuación al público') ||
      c.name.toLowerCase().includes('propósito claro') ||
      c.name.toLowerCase().includes('suficiencia') ||
      c.name.toLowerCase().includes('superflua')
    );

    // 2. FACILIDAD DE BÚSQUEDA: ¿Se encuentra fácilmente la información?
    const facilidadBusquedaCriteria = allCriteria.filter((c: any) =>
      (c.name.toLowerCase().includes('estructura clara') && c.name.toLowerCase().includes('títulos')) ||
      c.name.toLowerCase().includes('listas') ||
      c.name.toLowerCase().includes('viñetas') ||
      c.name.toLowerCase().includes('ilustraciones') ||
      (c.name.toLowerCase().includes('gráficos') && c.name.toLowerCase().includes('iconos'))
    );

    // 3. COMPRENSIBILIDAD: ¿Se entiende el texto?
    const comprensibilidadCriteria = allCriteria.filter((c: any) => {
      if (c.name.toLowerCase().includes('listas') ||
          c.name.toLowerCase().includes('viñetas') ||
          c.name.toLowerCase().includes('ilustraciones') ||
          c.name.toLowerCase().includes('gráficos') ||
          (c.name.toLowerCase().includes('iconos') && c.name.toLowerCase().includes('glosario'))) {
        return false;
      }

      return (
        ['legibilidad', 'vocabulario', 'claridad'].includes(c.category?.toLowerCase()) ||
        (c.category?.toLowerCase() === 'estructura' &&
         (c.name.toLowerCase().includes('voz activa') ||
          c.name.toLowerCase().includes('conectores') ||
          c.name.toLowerCase().includes('subordinadas'))) ||
        (c.category?.toLowerCase() === 'formato' && c.name.toLowerCase().includes('mayúsculas')) ||
        c.name.toLowerCase().includes('inflesz') ||
        c.name.toLowerCase().includes('glosario') ||
        c.name.toLowerCase().includes('negaciones') ||
        c.name.toLowerCase().includes('indeterminadas') ||
        c.name.toLowerCase().includes('tono y estilo')
      );
    });

    // 4. USABILIDAD: ¿Puede el lector usar la información para actuar?
    const usabilidadCriteria = allCriteria.filter((c: any) =>
      c.name.toLowerCase().includes('validación') ||
      c.name.toLowerCase().includes('usuarios reales') ||
      c.name.toLowerCase().includes('orientación a la acción') ||
      c.name.toLowerCase().includes('siguientes pasos')
    );

    // Función para renderizar un criterio
    const renderCriterion = (criterion: any, index: number) => {
      const [valor, explicacion] = criterion.description?.split(' | ') || [criterion.description, ''];
      const isManual = !criterion.scientific;
      const criterionKey = `${criterion.name}-${index}`;

      // Determinar el estado de validación
      const validation = criteriaValidations[criterionKey] !== undefined
        ? criteriaValidations[criterionKey]
        : (isManual ? null : criterion.status === 'pass');

      const handleValidationChange = (passes: boolean) => {
        const updatedValidations = {
          ...criteriaValidations,
          [criterionKey]: passes
        };
        setCriteriaValidations(updatedValidations);

        // Save to database
        saveValidationsMutation.mutate(updatedValidations);
      };

      return (
        <div
          key={index}
          className={`p-4 rounded-lg border-2 ${
            validation === true ? 'bg-green-50 border-green-200' :
            validation === false ? 'bg-red-50 border-red-200' :
            'bg-gray-50 border-gray-300'
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">
                  {validation === true ? (
                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                  ) : validation === false ? (
                    <FontAwesomeIcon icon={faTimesCircle} className="text-red-600" />
                  ) : (
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-gray-400" />
                  )}
                </span>
                <h4 className="font-semibold text-gray-900">{criterion.name}</h4>
                <span className="text-xs px-2 py-0.5 bg-gray-200 rounded-full text-gray-600">
                  {criterion.category}
                </span>
                {isManual && (
                  <span className="text-xs px-2 py-0.5 bg-amber-100 rounded-full text-amber-700">
                    manual
                  </span>
                )}
                {!isManual && (
                  <span className="text-xs px-2 py-0.5 bg-blue-100 rounded-full text-blue-700">
                    automático
                  </span>
                )}
                {criterion.weight && criterion.weight > 0 && criterion.scientific && (
                  <span className="text-xs px-2 py-0.5 bg-purple-100 rounded-full text-purple-700 font-semibold">
                    Pondera {criterion.weight}%
                  </span>
                )}
              </div>
              <div className="ml-7">
                <p className="text-sm font-medium text-gray-700">{valor}</p>
                {explicacion && (
                  <p className="text-xs text-gray-500 mt-1 italic">{explicacion}</p>
                )}
              </div>
            </div>
            <div className="flex gap-2 ml-4">
              <button
                onClick={() => handleValidationChange(true)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border-2 transition-all ${
                  validation === true
                    ? 'bg-green-500 border-green-600 text-white shadow-md'
                    : 'bg-white border-green-300 text-green-700 hover:bg-green-50'
                }`}
              >
                <FontAwesomeIcon icon={faCheckCircle} className="text-sm" />
                <span className="text-xs font-semibold">Pasa</span>
              </button>
              <button
                onClick={() => handleValidationChange(false)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border-2 transition-all ${
                  validation === false
                    ? 'bg-red-500 border-red-600 text-white shadow-md'
                    : 'bg-white border-red-300 text-red-700 hover:bg-red-50'
                }`}
              >
                <FontAwesomeIcon icon={faTimesCircle} className="text-sm" />
                <span className="text-xs font-semibold">No Pasa</span>
              </button>
            </div>
          </div>
        </div>
      );
    };

    // Función para calcular si un principio pasa
    const calculatePrincipleStatus = (criteria: any[], useWeights: boolean = false) => {
      if (criteria.length === 0) return { passes: false, percentage: 0, validated: 0, total: 0, score: 0, maxScore: 0 };

      if (useWeights) {
        let earnedScore = 0;
        let maxScore = 0;

        criteria.forEach((criterion, index) => {
          const criterionKey = `${criterion.name}-${index}`;
          const validation = criteriaValidations[criterionKey] !== undefined
            ? criteriaValidations[criterionKey]
            : (!criterion.scientific ? null : criterion.status === 'pass');

          const weight = criterion.weight || 0;
          maxScore += weight;

          if (validation === true) {
            earnedScore += weight;
          }
        });

        const percentage = maxScore > 0 ? (earnedScore / maxScore) * 100 : 0;
        return {
          passes: percentage >= 85,
          percentage: percentage,
          validated: Math.round(earnedScore),
          total: maxScore,
          score: earnedScore,
          maxScore: maxScore
        };
      } else {
        let passedCount = 0;
        let totalCriteria = criteria.length;

        criteria.forEach((criterion, index) => {
          const criterionKey = `${criterion.name}-${index}`;
          const validation = criteriaValidations[criterionKey] !== undefined
            ? criteriaValidations[criterionKey]
            : (!criterion.scientific ? null : criterion.status === 'pass');

          if (validation === true) {
            passedCount++;
          }
        });

        const percentage = (passedCount / totalCriteria) * 100;
        return {
          passes: percentage >= 85,
          percentage: percentage,
          validated: passedCount,
          total: totalCriteria,
          score: passedCount,
          maxScore: totalCriteria
        };
      }
    };

    // Calcular estado de cada principio
    const pertinenciaStatus = calculatePrincipleStatus([
      ...([
        {
          name: 'Adecuación al Público Objetivo',
          category: 'pertinencia',
          description: 'Verificar que el documento se ha escrito pensando en el perfil del lector | Evaluar conocimientos previos, contexto sociocultural y necesidades específicas del público objetivo',
          scientific: false,
          status: null
        },
        {
          name: 'Propósito Claro',
          category: 'pertinencia',
          description: 'Evaluar si el documento responde directamente al objetivo del lector | El lector debe poder tomar una decisión, realizar un trámite o entender un concepto específico',
          scientific: false,
          status: null
        },
        {
          name: 'Suficiencia de la Información',
          category: 'pertinencia',
          description: 'Verificar que contiene toda la información clave sin incluir contenido superfluo | Ni más ni menos: solo lo necesario para cumplir el propósito sin generar ruido informativo',
          scientific: false,
          status: null
        }
      ]),
      ...pertinenciaCriteria
    ], false); // Sin pesos

    const facilidadStatus = calculatePrincipleStatus(facilidadBusquedaCriteria, false); // Sin pesos
    const comprensibilidadStatus = calculatePrincipleStatus(comprensibilidadCriteria, true); // CON pesos ponderados
    const usabilidadStatus = calculatePrincipleStatus(usabilidadCriteria, false); // Sin pesos

    // Determinar si cumple con Lenguaje Claro
    const cumpleLenguajeClaro = pertinenciaStatus.passes && facilidadStatus.passes && comprensibilidadStatus.passes && usabilidadStatus.passes;

    return (
      <div className="space-y-6">
        {/* Header con validador de Lenguaje Claro */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Evaluación según UNE-ISO 24495-1:2024</h2>
              <p className="text-sm text-gray-600">
                Criterios organizados por los 4 principios de Lenguaje Claro
              </p>
            </div>
            <div className={`px-6 py-3 rounded-lg text-sm font-bold border-2 ${
              cumpleLenguajeClaro
                ? 'bg-green-100 text-green-800 border-green-400'
                : 'bg-red-100 text-red-800 border-red-400'
            }`}>
              {cumpleLenguajeClaro ? '✓ CUMPLE LENGUAJE CLARO' : '✗ NO CUMPLE LENGUAJE CLARO'}
            </div>
          </div>
        </div>

        {/* 1. PERTINENCIA */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">
                Principio 1. Pertinencia
              </h2>
              <div className={`px-4 py-2 rounded-lg font-semibold text-sm ${
                pertinenciaStatus.passes
                  ? 'bg-green-100 text-green-800 border-2 border-green-300'
                  : 'bg-red-100 text-red-800 border-2 border-red-300'
              }`}>
                {pertinenciaStatus.passes ? '✓ PASA' : '✗ NO PASA'} ({pertinenciaStatus.validated}/{pertinenciaStatus.total} - {pertinenciaStatus.percentage.toFixed(0)}%)
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-2 font-semibold">
              ¿Es la información que el lector necesita?
            </p>
            <p className="text-sm text-gray-600 mb-4">
              El documento debe contener la información que el lector necesita (ni más, ni menos) para cumplir su objetivo.
              Se identifica el <strong>público objetivo</strong>, su <strong>contexto</strong> y el <strong>propósito de la lectura</strong>.
            </p>

            <div className="space-y-3">
              {[
                {
                  name: 'Adecuación al Público Objetivo',
                  category: 'pertinencia',
                  description: 'Verificar que el documento se ha escrito pensando en el perfil del lector | Evaluar conocimientos previos, contexto sociocultural y necesidades específicas del público objetivo',
                  scientific: false,
                  status: null
                },
                {
                  name: 'Propósito Claro',
                  category: 'pertinencia',
                  description: 'Evaluar si el documento responde directamente al objetivo del lector | El lector debe poder tomar una decisión, realizar un trámite o entender un concepto específico',
                  scientific: false,
                  status: null
                },
                {
                  name: 'Suficiencia de la Información',
                  category: 'pertinencia',
                  description: 'Verificar que contiene toda la información clave sin incluir contenido superfluo | Ni más ni menos: solo lo necesario para cumplir el propósito sin generar ruido informativo',
                  scientific: false,
                  status: null
                }
              ].map(renderCriterion)}

              {pertinenciaCriteria.map(renderCriterion)}
            </div>
          </CardContent>
        </Card>

        {/* 2. FACILIDAD DE BÚSQUEDA */}
        {facilidadBusquedaCriteria.length > 0 && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">
                  Principio 2. Facilidad de Búsqueda (Localización)
                </h2>
                <div className={`px-4 py-2 rounded-lg font-semibold text-sm ${
                  facilidadStatus.passes
                    ? 'bg-green-100 text-green-800 border-2 border-green-300'
                    : 'bg-red-100 text-red-800 border-2 border-red-300'
                }`}>
                  {facilidadStatus.passes ? '✓ PASA' : '✗ NO PASA'} ({facilidadStatus.validated}/{facilidadStatus.total} - {facilidadStatus.percentage.toFixed(0)}%)
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-2 font-semibold">
                ¿Se encuentra fácilmente la información?
              </p>
              <p className="text-sm text-gray-600 mb-4">
                La información debe estar <strong>organizada y estructurada</strong> de forma lógica mediante:
                títulos claros, subtítulos jerárquicos, listas con viñetas, uso estratégico de negritas,
                espaciado adecuado, y <strong>apoyos visuales</strong> (ilustraciones, gráficos, iconos).
              </p>
              <div className="space-y-3">
                {facilidadBusquedaCriteria.map(renderCriterion)}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 3. COMPRENSIBILIDAD */}
        {comprensibilidadCriteria.length > 0 && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">
                  Principio 3. Comprensibilidad (Nivel B2 del MCERL)
                </h2>
                <div className={`px-4 py-2 rounded-lg font-semibold text-sm ${
                  comprensibilidadStatus.passes
                    ? 'bg-green-100 text-green-800 border-2 border-green-300'
                    : 'bg-red-100 text-red-800 border-2 border-red-300'
                }`}>
                  {comprensibilidadStatus.passes ? '✓ PASA' : '✗ NO PASA'} ({comprensibilidadStatus.validated}/{comprensibilidadStatus.total} - {comprensibilidadStatus.percentage.toFixed(0)}%)
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-2 font-semibold">
                ¿Se entiende el texto?
              </p>
              <p className="text-sm text-gray-600 mb-4">
                El texto debe usar <strong>palabras comunes</strong> (A1-B2), evitar jerga técnica,
                escribir <strong>frases cortas y directas</strong> (15-20 palabras máximo) con una sola idea principal,
                priorizar <strong>voz activa</strong>, limitar subordinadas,
                usar conectores simples, evitar palabras vagas y negaciones dobles,
                y transmitir un <strong>tono respetuoso</strong> y cercano.
              </p>
              <div className="space-y-3">
                {comprensibilidadCriteria.map(renderCriterion)}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Detailed Analysis */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Passive Voice */}
          {metadata.b2Criteria.passiveVoice?.count > 0 && (
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h4 className="font-semibold mb-2">Voz Pasiva Detectada</h4>
              <p className="text-sm text-gray-700 mb-2">
                {metadata.b2Criteria.passiveVoice.count} construcciones ({metadata.b2Criteria.passiveVoice.percentage.toFixed(1)}%)
              </p>
              {metadata.b2Criteria.passiveVoice.examples?.length > 0 && (
                <div className="text-xs text-gray-600 space-y-1">
                  <div className="font-semibold">Ejemplos:</div>
                  {metadata.b2Criteria.passiveVoice.examples.slice(0, 3).map((example: string, i: number) => (
                    <div key={i} className="italic">"{example}"</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Complex Connectors */}
          {metadata.b2Criteria.complexConnectors?.count > 0 && (
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-semibold mb-2">Conectores Complejos</h4>
              <p className="text-sm text-gray-700 mb-2">
                {metadata.b2Criteria.complexConnectors.count} tipos encontrados
              </p>
              <div className="text-xs text-gray-600 flex flex-wrap gap-1">
                {metadata.b2Criteria.complexConnectors.connectors?.slice(0, 5).map((conn: string, i: number) => (
                  <span key={i} className="px-2 py-0.5 bg-purple-100 rounded">{conn}</span>
                ))}
              </div>
            </div>
          )}

          {/* Uppercase Words */}
          {metadata.b2Criteria.uppercaseWords?.count > 0 && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold mb-2">Palabras en Mayúsculas</h4>
              <p className="text-sm text-gray-700 mb-2">
                {metadata.b2Criteria.uppercaseWords.count} palabras ({metadata.b2Criteria.uppercaseWords.percentage.toFixed(1)}%)
              </p>
              <div className="text-xs text-gray-600 flex flex-wrap gap-1">
                {metadata.b2Criteria.uppercaseWords.words?.slice(0, 10).map((word: string, i: number) => (
                  <span key={i} className="px-2 py-0.5 bg-blue-100 rounded">{word}</span>
                ))}
              </div>
            </div>
          )}

          {/* Vague Words */}
          {metadata.b2Criteria.vagueWords?.count > 0 && (
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <h4 className="font-semibold mb-2">Palabras Indeterminadas</h4>
              <p className="text-sm text-gray-700 mb-2">
                {metadata.b2Criteria.vagueWords.count} palabras vagas
              </p>
              <div className="text-xs text-gray-600 flex flex-wrap gap-1">
                {metadata.b2Criteria.vagueWords.words?.slice(0, 10).map((word: string, i: number) => (
                  <span key={i} className="px-2 py-0.5 bg-orange-100 rounded">{word}</span>
                ))}
              </div>
            </div>
          )}

          {/* Double Negations */}
          {metadata.b2Criteria.doubleNegations?.count > 0 && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <h4 className="font-semibold mb-2">Negaciones Dobles</h4>
              <p className="text-sm text-gray-700 mb-2">
                {metadata.b2Criteria.doubleNegations.count} construcciones detectadas
              </p>
              {metadata.b2Criteria.doubleNegations.examples?.length > 0 && (
                <div className="text-xs text-gray-600 space-y-1">
                  {metadata.b2Criteria.doubleNegations.examples.slice(0, 3).map((example: string, i: number) => (
                    <div key={i} className="italic">"{example}"</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Subordinate Clauses */}
          {metadata.b2Criteria.subordinateClauses?.count > 0 && (
            <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <h4 className="font-semibold mb-2">Oraciones Subordinadas</h4>
              <p className="text-sm text-gray-700 mb-2">
                {metadata.b2Criteria.subordinateClauses.count} subordinadas
                (promedio: {metadata.b2Criteria.subordinateClauses.avgPerSentence?.toFixed(2)}/oración)
              </p>
              {metadata.b2Criteria.subordinateClauses.examples?.length > 0 && (
                <div className="text-xs text-gray-600 space-y-1">
                  <div className="font-semibold">Ejemplos:</div>
                  {metadata.b2Criteria.subordinateClauses.examples.slice(0, 2).map((example: string, i: number) => (
                    <div key={i} className="italic">"{example}"</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 4. USABILIDAD */}
        {usabilidadCriteria.length > 0 && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">
                  Principio 4. Usabilidad
                </h2>
                <div className={`px-4 py-2 rounded-lg font-semibold text-sm ${
                  usabilidadStatus.passes
                    ? 'bg-green-100 text-green-800 border-2 border-green-300'
                    : 'bg-red-100 text-red-800 border-2 border-red-300'
                }`}>
                  {usabilidadStatus.passes ? '✓ PASA' : '✗ NO PASA'} ({usabilidadStatus.validated}/{usabilidadStatus.total} - {usabilidadStatus.percentage.toFixed(0)}%)
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-2 font-semibold">
                ¿Puede el lector usar la información para actuar?
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Este principio verifica que <strong>los tres anteriores han funcionado en la práctica</strong>.
                El documento debe permitir al lector <strong>usar la información</strong> para tomar decisiones,
                completar tareas o entender sus derechos y obligaciones.
                Se valida mediante <strong>pruebas con usuarios reales</strong> del público objetivo.
              </p>
              <div className="space-y-3">
                {usabilidadCriteria.map(renderCriterion)}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }


  // Tab: Recomendaciones
  function renderRecommendationsTab() {
    if (!metadata.b2Criteria) {
      return <div className="text-center text-gray-500 py-8">No hay datos disponibles para generar recomendaciones</div>;
    }

    const recommendations: Array<{
      priority: 'high' | 'medium' | 'low';
      title: string;
      description: string;
      actions: string[];
      examples?: string[];
      category: string;
    }> = [];

    const b2Data = metadata.b2Criteria;
    const allCriteria = b2Data.criteria || [];

    // Añadir los 3 criterios manuales de Pertinencia
    const manualPertinenciaCriteria = [
      {
        name: 'Adecuación al Público Objetivo',
        category: 'pertinencia',
        description: 'Verificar que el documento se ha escrito pensando en el perfil del lector | Evaluar conocimientos previos, contexto sociocultural y necesidades específicas del público objetivo',
        scientific: false,
        status: null
      },
      {
        name: 'Propósito Claro',
        category: 'pertinencia',
        description: 'Evaluar si el documento responde directamente al objetivo del lector | El lector debe poder tomar una decisión, realizar un trámite o entender un concepto específico',
        scientific: false,
        status: null
      },
      {
        name: 'Suficiencia de la Información',
        category: 'pertinencia',
        description: 'Verificar que contiene toda la información clave sin incluir contenido superfluo | Ni más ni menos: solo lo necesario para cumplir el propósito sin generar ruido informativo',
        scientific: false,
        status: null
      }
    ];

    // Clasificar criterios por principio UNE
    const pertinenciaCriteria = [
      ...manualPertinenciaCriteria,
      ...allCriteria.filter((c: any) =>
        c.name.toLowerCase().includes('adecuación al público') ||
        c.name.toLowerCase().includes('propósito claro') ||
        c.name.toLowerCase().includes('suficiencia') ||
        c.name.toLowerCase().includes('superflua')
      )
    ];

    const facilidadBusquedaCriteria = allCriteria.filter((c: any) =>
      (c.name.toLowerCase().includes('estructura clara') && c.name.toLowerCase().includes('títulos')) ||
      c.name.toLowerCase().includes('listas') ||
      c.name.toLowerCase().includes('viñetas') ||
      c.name.toLowerCase().includes('ilustraciones') ||
      (c.name.toLowerCase().includes('gráficos') && c.name.toLowerCase().includes('iconos'))
    );

    const comprensibilidadCriteria = allCriteria.filter((c: any) => {
      if (c.name.toLowerCase().includes('listas') ||
          c.name.toLowerCase().includes('viñetas') ||
          c.name.toLowerCase().includes('ilustraciones') ||
          c.name.toLowerCase().includes('gráficos') ||
          (c.name.toLowerCase().includes('iconos') && c.name.toLowerCase().includes('glosario'))) {
        return false;
      }

      return (
        ['legibilidad', 'vocabulario', 'claridad'].includes(c.category?.toLowerCase()) ||
        (c.category?.toLowerCase() === 'estructura' &&
         (c.name.toLowerCase().includes('voz activa') ||
          c.name.toLowerCase().includes('conectores') ||
          c.name.toLowerCase().includes('subordinadas'))) ||
        (c.category?.toLowerCase() === 'formato' && c.name.toLowerCase().includes('mayúsculas')) ||
        c.name.toLowerCase().includes('inflesz') ||
        c.name.toLowerCase().includes('glosario') ||
        c.name.toLowerCase().includes('negaciones') ||
        c.name.toLowerCase().includes('indeterminadas') ||
        c.name.toLowerCase().includes('tono y estilo')
      );
    });

    const usabilidadCriteria = allCriteria.filter((c: any) =>
      c.name.toLowerCase().includes('validación') ||
      c.name.toLowerCase().includes('usuarios reales') ||
      c.name.toLowerCase().includes('orientación a la acción') ||
      c.name.toLowerCase().includes('siguientes pasos')
    );

    // Combinar todos los criterios incluyendo manuales
    const allCriteriaWithManual = [...pertinenciaCriteria, ...facilidadBusquedaCriteria, ...comprensibilidadCriteria, ...usabilidadCriteria];

    // Analizar cada criterio que no pasa y generar recomendaciones
    allCriteriaWithManual.forEach((criterion: any, index: number) => {
      const criterionKey = `${criterion.name}-${index}`;
      const validation = criteriaValidations[criterionKey] !== undefined
        ? criteriaValidations[criterionKey]
        : (!criterion.scientific ? null : criterion.status === 'pass');

      // Solo generar recomendaciones para criterios que NO pasan
      if (validation === false) {
        // Determinar categoría del principio
        let category = '';
        if (pertinenciaCriteria.some((c: any) => c.name === criterion.name)) category = 'Pertinencia';
        else if (facilidadBusquedaCriteria.some((c: any) => c.name === criterion.name)) category = 'Facilidad de Búsqueda';
        else if (comprensibilidadCriteria.some((c: any) => c.name === criterion.name)) category = 'Comprensibilidad';
        else if (usabilidadCriteria.some((c: any) => c.name === criterion.name)) category = 'Usabilidad';

        // Generar recomendación específica según el criterio
        if (criterion.name.toLowerCase().includes('palabras por oración')) {
          const avgWords = section.avgWordsPerSentence ? Number(section.avgWordsPerSentence).toFixed(1) : 'N/A';
          const longSentences = b2Data.longSentences || [];
          recommendations.push({
            priority: 'high',
            title: 'Reducir longitud de oraciones',
            description: `El promedio es ${avgWords} palabras/oración. Se recomienda máximo 15-20 palabras.`,
            actions: [
              'Divide oraciones largas en dos o más oraciones cortas',
              'Elimina información redundante o secundaria',
              'Usa puntos en lugar de comas para separar ideas',
              'Revisa las oraciones que superan las 25 palabras'
            ],
            examples: longSentences.slice(0, 3).map((s: any) => `"${s.text}" (${s.wordCount} palabras)`),
            category
          });
        } else if (criterion.name.toLowerCase().includes('glosario')) {
          const glossaryTerms = metadata?.glossaryTerms?.terms || [];
          recommendations.push({
            priority: 'high',
            title: 'Agregar glosario de términos',
            description: `Se detectaron ${glossaryTerms.length} términos técnicos o especializados (niveles C1-C2).`,
            actions: [
              'Crea un glosario al final del documento',
              'Define cada término técnico de forma simple',
              'Vincula los términos en el texto al glosario',
              'Considera usar sinónimos más simples cuando sea posible'
            ],
            examples: glossaryTerms.slice(0, 5),
            category
          });
        } else {
          // Recomendación genérica para otros criterios
          recommendations.push({
            priority: 'medium',
            title: `Revisar: ${criterion.name}`,
            description: criterion.description?.split(' | ')[0] || 'Este criterio requiere atención.',
            actions: [
              'Revisa el criterio en la pestaña "Criterios"',
              'Consulta la normativa UNE-ISO 24495-1:2024',
              'Aplica las mejores prácticas de lenguaje claro',
              'Considera consultar con un experto en comunicación clara'
            ],
            category
          });
        }
      }
    });

    // Ordenar recomendaciones por prioridad
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    recommendations.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-lg font-bold text-blue-900 mb-2">📋 Recomendaciones para Mejorar</h3>
          <p className="text-sm text-blue-800">
            Esta pestaña muestra tareas concretas para mejorar los criterios que no cumplen con Lenguaje Claro.
            {recommendations.length === 0 && ' ¡Excelente! No hay recomendaciones pendientes.'}
          </p>
        </div>

        {recommendations.length === 0 ? (
          <Card className="bg-green-50 border-2 border-green-200">
            <CardContent className="p-6 text-center">
              <div className="text-6xl mb-4">✓</div>
              <h3 className="text-xl font-bold text-green-800 mb-2">
                ¡Felicitaciones!
              </h3>
              <p className="text-green-700">
                Todos los criterios evaluados cumplen con los estándares de Lenguaje Claro.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Resumen de recomendaciones */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-red-50 border-2 border-red-200">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-red-600">
                    {recommendations.filter(r => r.priority === 'high').length}
                  </div>
                  <div className="text-sm font-semibold text-red-800">Prioridad Alta</div>
                </CardContent>
              </Card>
              <Card className="bg-orange-50 border-2 border-orange-200">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-orange-600">
                    {recommendations.filter(r => r.priority === 'medium').length}
                  </div>
                  <div className="text-sm font-semibold text-orange-800">Prioridad Media</div>
                </CardContent>
              </Card>
              <Card className="bg-yellow-50 border-2 border-yellow-200">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-yellow-600">
                    {recommendations.filter(r => r.priority === 'low').length}
                  </div>
                  <div className="text-sm font-semibold text-yellow-800">Prioridad Baja</div>
                </CardContent>
              </Card>
            </div>

            {/* Lista de recomendaciones */}
            {recommendations.map((rec, idx) => (
              <Card
                key={idx}
                className={`border-2 ${
                  rec.priority === 'high' ? 'border-red-300 bg-red-50' :
                  rec.priority === 'medium' ? 'border-orange-300 bg-orange-50' :
                  'border-yellow-300 bg-yellow-50'
                }`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                          rec.priority === 'high' ? 'bg-red-200 text-red-800' :
                          rec.priority === 'medium' ? 'bg-orange-200 text-orange-800' :
                          'bg-yellow-200 text-yellow-800'
                        }`}>
                          {rec.priority === 'high' ? '🔴 ALTA' : rec.priority === 'medium' ? '🟠 MEDIA' : '🟡 BAJA'}
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 font-semibold">
                          {rec.category}
                        </span>
                      </div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">{rec.title}</h4>
                      <p className="text-sm text-gray-700 mb-3">{rec.description}</p>
                    </div>
                  </div>

                  <div className="mb-3">
                    <h5 className="text-sm font-semibold text-gray-800 mb-2">✅ Acciones recomendadas:</h5>
                    <ul className="space-y-1">
                      {rec.actions.map((action, actionIdx) => (
                        <li key={actionIdx} className="text-sm text-gray-700 flex items-start gap-2">
                          <span className="text-blue-600 mt-0.5">•</span>
                          <span>{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {rec.examples && rec.examples.length > 0 && (
                    <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                      <h5 className="text-sm font-semibold text-gray-800 mb-2">📝 Ejemplos detectados:</h5>
                      <div className="space-y-1">
                        {rec.examples.map((example, exIdx) => (
                          <div key={exIdx} className="text-xs text-gray-600 font-mono bg-gray-50 p-2 rounded">
                            {example}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }


  // Tab: Legibilidad
  // @ts-expect-error - Function kept for potential future use
  function _renderReadabilityTab() {
    return (
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-bold mb-4">Fórmula de Legibilidad INFLESZ</h2>
          <p className="text-sm text-gray-600 mb-6">
            INFLESZ es la fórmula de legibilidad adaptada al español que calcula la complejidad textual.
            Escala: 0-40 (muy difícil), 40-55 (algo difícil), 55-65 (normal), 65-80 (fácil), 80-100 (muy fácil).
          </p>

          {/* INFLESZ */}
          <div className="bg-purple-50 p-6 rounded-lg border border-purple-200 max-w-2xl mx-auto">
            <h3 className="text-lg font-bold text-purple-900 mb-4">INFLESZ (Adaptación Española)</h3>

            <div className="bg-white p-4 rounded-lg mb-4">
              <div className="text-sm text-gray-600 mb-2"><strong>Fórmula:</strong></div>
              <div className="font-mono text-xs bg-gray-100 p-2 rounded">
                206.84 - (1.02 × P/F) - (60 × S/P)
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg mb-4">
              <div className="text-sm text-gray-600 mb-2"><strong>Donde:</strong></div>
              <ul className="text-xs space-y-1">
                <li>• P/F = Palabras por frase ({section.avgWordsPerSentence != null ? Number(section.avgWordsPerSentence).toFixed(1) : 'N/A'})</li>
                <li>• S/P = Sílabas por palabra ({section.avgSyllablesPerWord != null ? Number(section.avgSyllablesPerWord).toFixed(2) : 'N/A'})</li>
              </ul>
            </div>

            <div className="bg-white p-4 rounded-lg">
              <div className="text-sm text-gray-600 mb-2"><strong>Cálculo:</strong></div>
              <div className="font-mono text-xs bg-gray-100 p-2 rounded mb-2">
                206.84 - ({(Number(section.avgWordsPerSentence) * 1.02).toFixed(1)}) - ({(Number(section.avgSyllablesPerWord) * 60).toFixed(1)}) = {section.infleszScore != null ? Number(section.infleszScore).toFixed(1) : 'N/A'}
              </div>
              <div className="mt-3">
                <div className="text-xs font-semibold">Resultado: <span className="text-2xl text-purple-600">{section.infleszScore != null ? Number(section.infleszScore).toFixed(1) : 'N/A'} puntos</span></div>
                <div className="text-xs mt-1">Interpretación: <span className={`font-semibold ${Number(section.infleszScore) >= 55 ? 'text-green-600' : 'text-red-600'}`}>
                  {Number(section.infleszScore) >= 55 ? <><FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Cumple (≥55 req.)</> : <><FontAwesomeIcon icon={faTimesCircle} className="mr-1" /> No cumple (≥55 req.)</>}
                </span></div>
                <div className="text-xs mt-1"><FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mr-1" />Cobertura: 100% del vocabulario</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Tab: Vocabulario
  function renderVocabularyTab() {
    if (!metadata.vocabularyDistribution) {
      return <div className="text-center text-gray-500 py-8">No hay datos de vocabulario disponibles</div>;
    }

    return (
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-bold mb-4">Análisis del Vocabulario</h2>

          {/* Words by Level */}
          {metadata.wordsByLevel && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {Object.entries(metadata.wordsByLevel).map(([level, words]: [string, any]) => {
                const colorClasses = {
                  A1: 'bg-green-500',
                  A2: 'bg-teal-500',
                  B1: 'bg-yellow-500',
                  B2: 'bg-orange-500',
                  C1: 'bg-red-500',
                  C2: 'bg-purple-500'
                };
                const percentage = metadata.vocabularyDistribution[level] || 0;

                return (
                  <div key={level} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className={`text-center mb-2 px-3 py-1 rounded text-white ${colorClasses[level as keyof typeof colorClasses]}`}>
                      Nivel {level} ({percentage.toFixed(1)}%)
                    </div>
                    <div className="text-xs text-gray-600 max-h-40 overflow-y-auto">
                      {Array.isArray(words) && words.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {words.slice(0, 15).map((word: string, i: number) => (
                            <span key={i} className="px-2 py-0.5 bg-gray-200 rounded text-xs">{word}</span>
                          ))}
                          {words.length > 15 && <span className="text-gray-400">... y {words.length - 15} más</span>}
                        </div>
                      ) : (
                        <div className="text-center text-gray-400">Sin palabras</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Tab: Glosario
  function renderGlossaryTab() {
    if (!metadata.glossaryTerms) {
      return <div className="text-center text-gray-500 py-8">No hay datos de glosario disponibles</div>;
    }

    const { glossaryTerms, acronyms } = metadata;

    return (
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-bold mb-4">Términos que Requieren Glosario</h2>

          {glossaryTerms.count > 0 ? (
            <div className="space-y-6">
              {/* Siglas Detectadas */}
              {acronyms && acronyms.list && acronyms.list.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <span className="text-xl">🔤</span>
                    Siglas y Abreviaturas ({acronyms.list.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {acronyms.list.map((acronym: string, i: number) => (
                      <span key={i} className="px-3 py-2 bg-blue-100 text-blue-900 rounded-lg text-sm font-semibold border border-blue-300">
                        {acronym}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Todos los Términos */}
              <div>
                <h4 className="font-semibold mb-3">Todos los Términos de Glosario ({glossaryTerms.count})</h4>
                <div className="flex flex-wrap gap-2">
                  {glossaryTerms.terms.map((term: string, i: number) => (
                    <span key={i} className="px-3 py-2 bg-orange-100 text-orange-900 rounded-lg text-sm font-medium border border-orange-200">
                      {term}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-green-800 font-semibold mb-2">¡Excelente!</h4>
              <p className="text-sm text-green-700">No se encontraron términos que requieran glosario.</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Tab: Morfología
  // @ts-expect-error - Function kept for potential future use
  function _renderMorphologyTab() {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-500 py-8">
          No hay datos morfológicos disponibles para esta sección
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-600 rounded-xl p-8 shadow-lg">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-white/20 text-white border-white/30">
                {sectionLabelMap[section.sectionType]}
              </Badge>
              <Badge className="bg-white/20 text-white border-white/30">
                {section.analysis.sourceType}
              </Badge>
              {section.analysis.sector && (
                <Badge className="bg-white/20 text-white border-white/30">
                  {section.analysis.sector.icon} {section.analysis.sector.name}
                </Badge>
              )}
              {/* Audit Status Badge - SECTION status */}
              <Badge className={`flex items-center gap-1.5 ${
                section.auditStatus === 'AUDITED'
                  ? 'bg-green-500/90 text-white border-green-400'
                  : 'bg-gray-400/90 text-white border-gray-300'
              }`}>
                <span className={`w-2 h-2 rounded-full ${
                  section.auditStatus === 'AUDITED' ? 'bg-green-200' : 'bg-gray-200'
                }`}></span>
                {section.auditStatus === 'AUDITED' ? 'Auditado' : 'Sin Auditar'}
              </Badge>
            </div>
            <h1 className="text-3xl font-bold mb-2 text-white">
              {section.title || sectionLabelMap[section.sectionType]}
            </h1>
            <p className="text-white/90">
              {section.analysis.title} • {new Date(section.analysis.createdAt).toLocaleDateString('es-ES')}
            </p>
          </div>
        </div>
      </div>

      {/* Actions - Moved to top */}
      <div className="flex gap-4">
        <Button onClick={() => {
          // Navigate to sections list based on source type
          // Document types: PDF, DOCX, TXT, TSV
          if (['PDF', 'DOCX', 'TXT', 'TSV'].includes(section.analysis.sourceType)) {
            navigate(`/analysis/document/${section.analysis.id}/sections`);
          } else if (section.analysis.sourceType === 'URL') {
            navigate(`/analysis/web/${section.analysis.id}/sections`);
          } else {
            // Fallback to analysis page for MANUAL type
            navigate(`/analysis/${section.analysis.id}`);
          }
        }} variant="outline">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Volver al documento
        </Button>
        <Button
          onClick={() => toggleAuditStatusMutation.mutate()}
          disabled={toggleAuditStatusMutation.isPending}
          className={section.auditStatus === 'AUDITED' ? 'bg-gray-500 hover:bg-gray-600' : 'bg-green-600 hover:bg-green-700'}
        >
          <FontAwesomeIcon
            icon={section.auditStatus === 'AUDITED' ? faTimesCircle : faCheckCircle}
            className="mr-2"
          />
          {toggleAuditStatusMutation.isPending
            ? 'Actualizando...'
            : section.auditStatus === 'AUDITED'
              ? 'Marcar como Sin Auditar'
              : 'Marcar como Auditado'
          }
        </Button>
      </div>

      {/* Tabs */}
      <Card>
        <CardContent className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className={`grid w-full mb-6 ${preferencesData?.preferences?.enableAiSimplification ? 'grid-cols-6' : 'grid-cols-5'}`}>
              <TabsTrigger value="overview">Resumen</TabsTrigger>
              <TabsTrigger value="b2-criteria">Criterios</TabsTrigger>
              <TabsTrigger value="vocabulary">Corpus Léxico</TabsTrigger>
              <TabsTrigger value="glossary">Glosario</TabsTrigger>
              <TabsTrigger value="recommendations">Recomendaciones</TabsTrigger>
              {preferencesData?.preferences?.enableAiSimplification && (
                <TabsTrigger value="ai-simplification">Simplificación IA</TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="overview">{renderOverviewTab()}</TabsContent>
            <TabsContent value="b2-criteria">{renderB2CriteriaTab()}</TabsContent>
            <TabsContent value="vocabulary">{renderVocabularyTab()}</TabsContent>
            <TabsContent value="glossary">{renderGlossaryTab()}</TabsContent>
            <TabsContent value="recommendations">{renderRecommendationsTab()}</TabsContent>
            {preferencesData?.preferences?.enableAiSimplification && (
              <TabsContent value="ai-simplification">
                <div className="text-center text-gray-500 py-8">
                  La simplificación con IA está disponible mediante el botón "Simplificar con IA" en la pestaña de Resumen.
                </div>
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>

      {/* Simplification Modal */}
      <SimplificationModal
        isOpen={showSimplificationModal}
        onClose={() => setShowSimplificationModal(false)}
        originalText={simplificationResult?.originalText || section.content}
        simplifiedText={simplificationResult?.simplifiedText}
        title={`Simplificación: ${section.title || sectionLabelMap[section.sectionType]}`}
        isLoading={simplifyMutation.isPending}
        provider={simplificationResult?.provider}
        targetLevel={simplificationResult?.targetLevel}
      />
    </div>
  );
}

interface _MetricCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: 'blue' | 'purple' | 'green';
}

// @ts-expect-error - Component kept for potential future use
function _MetricCard({ title, value, icon, color }: _MetricCardProps) {
  const colorClasses = {
    blue: { bg: 'bg-blue-500', text: 'text-blue-600' },
    purple: { bg: 'bg-fuchsia-500', text: 'text-fuchsia-600' },
    green: { bg: 'bg-emerald-500', text: 'text-emerald-600' },
  };

  const classes = colorClasses[color];

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className={`w-12 h-12 ${classes.bg} rounded-full flex items-center justify-center text-white text-lg shrink-0`}>
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-muted-foreground font-medium mb-1">{title}</p>
            <p className={`text-3xl font-bold ${classes.text}`}>{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
